import React, { useState } from 'react'
import styled from 'styled-components'
import TextField from 'core-system/TextField'
import Text from 'core-system/Text'
import Close from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'

/** Matches contained Dropdown min-height so controls align across columns. */
const CONTROL_MIN_HEIGHT = '3.5rem'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`

const InputSlot = styled.div`
  min-height: ${CONTROL_MIN_HEIGHT};
  display: flex;
  align-items: center;
  width: 100%;
`

const InputWrap = styled.div`
  width: 100%;
  input {
    border-radius: 0.5rem;
    border-color: ${(props) => props.theme.palette.grey.grey300};
    &:hover {
      border-color: ${(props) => props.theme.palette.grey.grey600};
    }
    &:focus {
      border-color: ${(props) => props.theme.palette.text.primary};
    }
  }
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.2rem 0.35rem 0.2rem 0.55rem;
  border-radius: 999px;
  background: ${(p) => p.theme.palette.grey.grey200};
  color: ${(p) => p.theme.palette.grey.grey800};
  font-size: 0.8125rem;
  line-height: 1.2;
`

export interface ListValueInputProps {
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  height?: string
}

const ListValueInput: React.FC<ListValueInputProps> = ({
  values,
  onChange,
  placeholder = 'Type a value and press Enter',
  height = '52px',
}) => {
  const [draft, setDraft] = useState('')

  const addValue = (raw: string) => {
    const next = raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
    if (!next.length) return
    const seen = new Set(values.map((v) => v.toLowerCase()))
    const merged = [...values]
    for (const item of next) {
      const key = item.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      merged.push(item)
    }
    onChange(merged)
    setDraft('')
  }

  const removeValue = (index: number) => {
    onChange(values.filter((_, i) => i !== index))
  }

  return (
    <Wrap>
      <InputSlot>
        <InputWrap>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onenter={(e) => {
              e.preventDefault()
              addValue(draft)
            }}
            placeholder={placeholder}
            height={height}
          />
        </InputWrap>
      </InputSlot>
      {values.length > 0 && (
        <Chips>
          {values.map((item, index) => (
            <Chip key={`${item}-${index}`}>
              {item}
              <IconButton
                size='small'
                aria-label={`Remove ${item}`}
                onClick={() => removeValue(index)}
                sx={{ padding: '2px' }}
              >
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Chip>
          ))}
        </Chips>
      )}
      <Text variant='caption' color='grey600'>
        Press Enter to add each value. Commas also split multiple values.
      </Text>
    </Wrap>
  )
}

export default React.memo(ListValueInput)
